import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Play } from 'lucide-react';
import { useState } from 'react';
import { DataTable } from '@/components/glitch-ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { toast } from '@/hooks/useToast';
import {
  ScheduledJob,
  useRunJobMutation,
  useSchedulerJobsQuery,
  useUpdateJobMutation,
} from '@/lib/api-extensions/scheduler-queries';
import { EditJobDialog } from './edit-job-dialog';

/** Render an ISO timestamp in the viewer's locale, or a dash when never run. */
function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

interface SchedulerJobsTableProps {
  organizationId: number | null; // null lists jobs across every organization
}

export function SchedulerJobsTable({ organizationId }: SchedulerJobsTableProps) {
  const { data: jobs, isLoading, error } = useSchedulerJobsQuery(organizationId);
  const updateJob = useUpdateJobMutation();
  const runJob = useRunJobMutation();
  const [editing, setEditing] = useState<ScheduledJob | null>(null);
  const [runConfirm, setRunConfirm] = useState<ScheduledJob | null>(null);

  const toggleEnabled = (job: ScheduledJob, enabled: boolean) => {
    updateJob.mutate(
      { jobId: job.id, update: { enabled } },
      {
        onSuccess: () =>
          toast({
            title: enabled ? 'Job enabled' : 'Job disabled',
            description: `${job.job_name} is now ${enabled ? 'enabled' : 'disabled'}.`,
          }),
        onError: (e) =>
          toast({
            title: 'Update failed',
            description: e instanceof Error ? e.message : 'Could not change the job.',
            variant: 'destructive',
          }),
      },
    );
  };

  const confirmRun = () => {
    if (!runConfirm) return;
    const job = runConfirm;
    runJob.mutate(job.id, {
      onSuccess: (res) => {
        toast({ title: 'Manual run started', description: `${job.job_name} is queued (run #${res.job_run_id}).` });
        setRunConfirm(null);
      },
      onError: (e) => {
        toast({
          title: 'Run failed',
          description: e instanceof Error ? e.message : 'Could not start the run.',
          variant: 'destructive',
        });
        setRunConfirm(null);
      },
    });
  };

  const columns: ColumnDef<ScheduledJob>[] = [
    {
      accessorKey: 'job_name',
      header: 'Job',
      cell: ({ row }) => <div className="font-medium text-gray-900">{row.getValue('job_name')}</div>,
    },
    {
      accessorKey: 'scope',
      header: 'Scope',
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-normal">
          {(row.getValue('scope') as string) === 'per_org' ? 'Per org' : 'Global'}
        </Badge>
      ),
    },
    {
      accessorKey: 'schedule',
      header: 'Schedule',
      cell: ({ row }) => <span className="font-mono text-sm text-gray-700">{row.getValue('schedule')}</span>,
    },
    {
      accessorKey: 'timezone',
      header: 'Timezone',
      cell: ({ row }) => <span className="text-gray-700">{row.getValue('timezone')}</span>,
    },
    {
      accessorKey: 'next_run_at',
      header: 'Next run',
      cell: ({ row }) => <span className="text-gray-700">{formatWhen(row.getValue('next_run_at'))}</span>,
    },
    {
      accessorKey: 'last_run_at',
      header: 'Last run',
      cell: ({ row }) => <span className="text-gray-500">{formatWhen(row.getValue('last_run_at'))}</span>,
    },
    {
      accessorKey: 'enabled',
      header: 'Enabled',
      cell: ({ row }) => {
        const job = row.original;
        return (
          <Switch
            checked={job.enabled}
            onCheckedChange={(v) => toggleEnabled(job, v)}
            disabled={updateJob.isPending}
          />
        );
      },
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const job = row.original;
        return (
          <div className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="cursor-pointer" onClick={() => setEditing(job)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer" onClick={() => setRunConfirm(job)}>
                  <Play className="mr-2 h-4 w-4" />
                  Run now
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  if (error) {
    return (
      <div className="rounded-lg border border-l-4 border-l-[#B91C1C] bg-[#FEE2E2] p-4 text-sm text-[#B91C1C]">
        Scheduled jobs could not be loaded. Confirm admin access and try again.
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading scheduled jobs...</div>;
  }

  return (
    <div>
      <DataTable data={jobs ?? []} columns={columns} itemsName="job" pageSize={25} />
      <EditJobDialog job={editing} open={!!editing} onClose={() => setEditing(null)} />
      <ConformationModal
        isOpen={!!runConfirm}
        onClose={() => setRunConfirm(null)}
        onConfirm={confirmRun}
        title="Run Job Now"
        description={
          runConfirm
            ? `Queue an immediate run of ${runConfirm.job_name}? This runs once, in addition to its schedule.`
            : ''
        }
        confirmText="Run now"
        cancelText="Cancel"
        isLoading={runJob.isPending}
      />
    </div>
  );
}
